import { cn } from "@simpro/ui";
import { useId } from "react";
import { type ThemeChoice, useThemeStore } from "@/lib/theme.store";

const CHOICES: readonly { value: ThemeChoice; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

export interface AppearanceSwitchProps {
  /** The visible label beside it ("Appearance"), which names the group. */
  labelledBy: string;
  /** On the dark footer the chosen part is light; on a card it is ink. */
  onBar?: boolean;
}

/**
 * Auto, Light or Dark (follow-up F18): a three-way switch, Auto by default, kept on the device so it
 * needs no sign-in. It lives in Account → Preferences and in the desktop footer; the header carries
 * only the one-tap light and dark `ThemeToggle` (Rosé Arch), and the booking flow neither. Native
 * radios underneath, so arrow keys move between the three.
 *
 * Restyled under design note salon-booking-premium-redesign.md section 5.3 item 16: a hairline track
 * like the site's switcher, the chosen part an ink pill (ivory on the dark footer), and the focus
 * ring drawn inside the track.
 */
export function AppearanceSwitch({ labelledBy, onBar = false }: AppearanceSwitchProps) {
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const name = useId();

  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className={cn(
        "flex shrink-0 gap-0.5 rounded-full p-1 ring-1 ring-inset",
        onBar ? "ring-on-bar/25" : "bg-surface ring-edge-subtle",
      )}
    >
      {CHOICES.map(({ value, label }) => {
        const checked = theme === value;
        return (
          <label
            key={value}
            className={cn(
              "focus-inset grid h-9 cursor-pointer place-items-center rounded-full px-3.5 text-[13px] font-semibold transition-colors duration-300 md:text-sm",
              checked
                ? onBar
                  ? "bg-on-bar text-bar"
                  : "bg-ink text-on-ink"
                : onBar
                  ? "text-on-bar/75 hover:text-on-bar"
                  : "text-muted hover:text-heading",
            )}
          >
            <input
              type="radio"
              name={name}
              value={value}
              checked={checked}
              onChange={() => setTheme(value)}
              className="sr-only"
            />
            {label}
          </label>
        );
      })}
    </div>
  );
}
